import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { bannersService } from '../services/api';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';

const BannerAddPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: '',
    priority: 0,
    status: 'active',
    start_date: '',
    end_date: ''
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadBanner();
    }
  }, [isEdit, id]);

  const loadBanner = async () => {
    try {
      setLoadingData(true);
      const response = await bannersService.getBanner(id);
      if (response.success) {
        const banner = response.data;
        setFormData({
          title: banner.title || '',
          description: banner.description || '',
          link_url: banner.link_url || '',
          priority: banner.priority || 0,
          status: banner.status || 'active',
          start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
          end_date: banner.end_date ? banner.end_date.split('T')[0] : ''
        });
        
        // Mevcut görseli göster
        const spacesEndpoint = process.env.REACT_APP_SPACES_ENDPOINT || 'https://randuapp.nyc3.digitaloceanspaces.com';
        setExistingImageUrl(`${spacesEndpoint}/${banner.image_url}`);
      } else {
        toast.error(response.error || 'Banner yüklenemedi');
        navigate('/banners');
      }
    } catch (error) {
      console.error('Error loading banner:', error);
      toast.error('Banner yüklenirken bir hata oluştu');
      navigate('/banners');
    } finally {
      setLoadingData(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // File size check (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan büyük olamaz');
        return;
      }

      // File type check
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Sadece resim dosyaları kabul edilir (JPG, PNG, GIF, WEBP)');
        return;
      }

      setSelectedImage(file);
      
      // Preview oluştur
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !selectedImage) {
      toast.error('Lütfen bir banner görseli seçin');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Başlık gereklidir');
      return;
    }

    try {
      setLoading(true);

      const uploadFormData = new FormData();
      
      // Yeni görsel seçildiyse ekle
      if (selectedImage) {
        uploadFormData.append('image', selectedImage);
      }
      
      uploadFormData.append('title', formData.title);
      uploadFormData.append('description', formData.description || '');
      uploadFormData.append('link_url', formData.link_url || '');
      uploadFormData.append('priority', formData.priority || 0);
      uploadFormData.append('status', formData.status);
      uploadFormData.append('start_date', formData.start_date || '');
      uploadFormData.append('end_date', formData.end_date || '');

      let response;
      if (isEdit) {
        response = await bannersService.updateBanner(id, uploadFormData);
      } else {
        response = await bannersService.createBanner(uploadFormData);
      }

      if (response.success) {
        toast.success(isEdit ? 'Banner başarıyla güncellendi' : 'Banner başarıyla oluşturuldu');
        navigate('/banners');
      } else {
        toast.error(response.error || (isEdit ? 'Banner güncellenemedi' : 'Banner oluşturulamadı'));
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error(isEdit ? 'Banner güncellenirken bir hata oluştu' : 'Banner oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <MasterLayout>
        <Breadcrumb title={isEdit ? "Banner Düzenle" : "Banner Ekle"} />
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title={isEdit ? "Banner Düzenle" : "Banner Ekle"} />
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">{isEdit ? "Banner Düzenle" : "Banner Ekle"}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Görsel Seçimi */}
            <div className="mb-4">
              <label className="form-label">
                Banner Görseli {!isEdit && <span className="text-danger">*</span>}
              </label>
              <input
                type="file"
                className="form-control"
                onChange={handleImageChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                required={!isEdit}
              />
              <small className="text-muted">
                Desteklenen formatlar: JPG, PNG, GIF, WEBP (Maksimum: 5MB)
              </small>
              
              {/* Preview */}
              {(previewImage || existingImageUrl) && (
                <div className="mt-3">
                  <img
                    src={previewImage || existingImageUrl}
                    alt="Banner Preview"
                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
            </div>

            {/* Başlık */}
            <div className="mb-4">
              <label className="form-label">Başlık <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Banner başlığı"
              />
            </div>

            {/* Açıklama */}
            <div className="mb-4">
              <label className="form-label">Açıklama</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Banner açıklaması (opsiyonel)"
              />
            </div>

            {/* Link URL */}
            <div className="mb-4">
              <label className="form-label">Link URL</label>
              <input
                type="url"
                className="form-control"
                value={formData.link_url}
                onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                placeholder="https://example.com (opsiyonel)"
              />
              <small className="text-muted">Banner'a tıklandığında gidilecek link</small>
            </div>

            {/* Öncelik ve Durum */}
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">Öncelik</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  min="0"
                  placeholder="0"
                />
                <small className="text-muted">Yüksek öncelik önce gösterilir</small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Durum</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
            </div>

            {/* Tarihler */}
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">Başlangıç Tarihi (Opsiyonel)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Bitiş Tarihi (Opsiyonel)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Butonlar */}
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {isEdit ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
                  </>
                ) : (
                  <>
                    <Icon icon="solar:check-circle-outline" className="me-2" />
                    {isEdit ? 'Güncelle' : 'Oluştur'}
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/banners')}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
};

export default BannerAddPage;

